import type { Configuration, RedirectRequest, SsoSilentRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_AAD_TENANT_ID ?? 'common';
const clientId = import.meta.env.VITE_AAD_CLIENT_ID ?? '00000000-0000-0000-0000-000000000000';
const apiScope = import.meta.env.VITE_API_SCOPE ?? 'api://reportportal/access_as_user';

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true
  },
  cache: {
    /**
     * localStorage rather than sessionStorage, which is per-tab: every new tab
     * and every browser restart started from an empty cache with no account in
     * it, so there was no "you are already signed in" path at all and the
     * hidden ssoSilent iframe was the only way back in on each cold start.
     * With localStorage MSAL finds the account and refresh token already there
     * and renews straight against the token endpoint - no iframe, no Entra
     * round trip anyone waits on.
     *
     * The trade is that tokens now outlive the browser session, widening the
     * window an XSS could steal one in. What makes that acceptable here is the
     * CSP in staticwebapp.config.json: script-src is 'self' with no
     * 'unsafe-inline' and no CDN, so there is no route for injected script to
     * execute in the first place. If that CSP is ever loosened, revisit this.
     */
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  },
  system: {
    /**
     * MSAL's default is 10s, and all of it is dead time on screen. ssoSilent
     * cannot report failure until the iframe times out, and useAutoSignIn
     * answers that failure by falling back to a full redirect - so whenever
     * the iframe cannot complete (tracking prevention, third-party cookie
     * blocking, an Entra session that genuinely needs a prompt) the user
     * watches "Signing you in..." for ten seconds before anything happens.
     *
     * A working silent round trip is a redirect off an Entra session cookie
     * and lands in well under a second, so 3s leaves generous headroom. Cutting
     * a slow-but-viable attempt short is not a new failure mode either: it ends
     * in the same redirect the timeout would have produced, just sooner.
     *
     * MSAL logs a warning on any value below its 10s default, so expect one
     * line per silent attempt in the console. That is the price of the setting,
     * not a fault.
     */
    iframeHashTimeout: 3000,
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
      piiLoggingEnabled: false
    }
  }
};

export const loginRequest: RedirectRequest = {
  scopes: [apiScope]
};

/**
 * Silent SSO against the Entra session the browser already has. Everyone at
 * Key Travel is signed in to Entra, so this normally satisfies the whole
 * sign-in without a redirect. No prompt is set: MSAL sends prompt=none.
 */
export const ssoSilentRequest: SsoSilentRequest = {
  scopes: [apiScope]
};

/**
 * Forces the Entra account picker. The manual escape hatches use this because
 * by the time a user reaches one the problem is usually "signed in as the
 * wrong account", which a plain loginRedirect would silently repeat.
 */
export const selectAccountRequest: RedirectRequest = {
  ...loginRequest,
  prompt: 'select_account'
};

export const apiScopes = [apiScope];
